import * as React from 'react';

import {
  CustomAttributeEntityType,
  enumFieldConstraintObjectType,
  enumFieldDataType,
  FieldConstraintObjectType,
  FieldDataType,
} from '@rematter/frontend-lib-api-client';
import { ensureValue, ConsoleDialog } from '@rematter/ui-web';
import {
  Button,
  Checkbox,
  Label,
  NumberField,
  TextField,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucideTrash2 } from 'lucide-react';

import * as state from '../../_state/customAttributes.state';
import { useTranslations } from '../../i18n/useTranslations';
import { CustomAttributeForm } from '../../types/customAttributes.types';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';

export const CustomAttributeDialog = () => {
  const t = useTranslations();

  const selectOptionsRef = React.useRef<HTMLDivElement>(null);
  const nameInputRef = React.useRef<HTMLDivElement>(null);

  const [
    {
      isPending: isUpdatingCustomAttribute,
      isSuccess: isCustomAttributeUpdated,
    },
    updateCustomAttribute,
  ] = useAtom(state.$updateCustomAttribute);

  const [
    {
      isPending: isCreatingCustomAttribute,
      isSuccess: isCustomAttributeCreated,
    },
    createCustomAttribute,
  ] = useAtom(state.$createCustomAttribute);

  const [customAttributes] = useAtom(state.$customAttributes);
  const [entityType] = useAtom(state.$entityType);
  const [formData, setFormData] = useAtom(state.$customAttributeForm);
  const [editingId, setEditingId] = useAtom(state.$customAttributeId);
  const [showCreationDialog, setShowCreationDialog] = useAtom(
    state.$showCreationDialog,
  );

  const shouldShowDialog = showCreationDialog || !!editingId;

  const {
    canHaveLengthConstraint,
    canHaveMinMaxValueConstraint,
    canHaveUniqueConstraint,
    canHaveObjectType,
    canHaveSelectOptions,
    canSubmitForm,
  } = useAtomValue(state.$customAttributeFormConstraints);

  const handleFieldChange =
    <F extends keyof CustomAttributeForm>(field: F) =>
    (value: CustomAttributeForm[F]) => {
      setFormData((form) => ({
        ...form,
        [field]: value,
      }));
    };

  const handleAddOption = () => {
    setFormData((form) => ({
      ...form,
      selectOptions: [...(form.selectOptions || []), ''],
    }));
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) setEditingId(undefined);
    setShowCreationDialog(open);
    setFormData({});
  };

  const handleCreate = () => {
    createCustomAttribute({
      dataType: ensureValue(formData.dataType),
      name: ensureValue(formData.name),
      entityType: ensureValue(entityType),
      order:
        (entityType &&
          customAttributes.data?.[entityType as CustomAttributeEntityType]
            ?.length) ??
        0,
      constraints: {
        maxLength: formData.maxLength,
        minLength: formData.minLength,
        maxValue: formData.maxValue,
        minValue: formData.minValue,
        multipleValues: formData.multipleValues,
        required: formData.required,
        unique: formData.unique,
        objectType: formData.objectType,
        selectOptions: formData.selectOptions,
      },
    });
  };

  const handleUpdate = () => {
    updateCustomAttribute({
      id: ensureValue(editingId),
      name: ensureValue(formData.name),
      constraints: {
        maxLength: formData.maxLength,
        minLength: formData.minLength,
        maxValue: formData.maxValue,
        minValue: formData.minValue,
        multipleValues: formData.multipleValues,
        required: formData.required,
        unique: formData.unique,
        objectType: formData.objectType,
        selectOptions: formData.selectOptions,
      },
    });
  };

  // 🔵🔵🔵 Clear states when custom attribute is created or updated
  React.useEffect(() => {
    if (isCustomAttributeCreated || isCustomAttributeUpdated) {
      setEditingId(undefined);
      setShowCreationDialog(false);
      setFormData({});
    }
  }, [
    isCustomAttributeCreated,
    isCustomAttributeUpdated,
    setEditingId,
    setFormData,
    setShowCreationDialog,
  ]);

  // 🔵🔵🔵 Reset fields that are not applicable to the current data type
  React.useEffect(() => {
    setFormData((form) => ({
      ...form,
      objectType: canHaveObjectType ? form.objectType : undefined,
      selectOptions: canHaveSelectOptions ? form.selectOptions : undefined,
      unique: canHaveUniqueConstraint ? form.unique : undefined,
      minValue: canHaveMinMaxValueConstraint ? form.minValue : undefined,
      maxValue: canHaveMinMaxValueConstraint ? form.maxValue : undefined,
      minLength: canHaveLengthConstraint ? form.minLength : undefined,
      maxLength: canHaveLengthConstraint ? form.maxLength : undefined,
    }));
  }, [
    canHaveLengthConstraint,
    canHaveMinMaxValueConstraint,
    canHaveObjectType,
    canHaveSelectOptions,
    canHaveUniqueConstraint,
    setFormData,
  ]);

  // 🔵🔵🔵 Reset form data when editing an existing custom attribute
  React.useEffect(() => {
    const customAttribute = entityType
      ? customAttributes.data?.[entityType as CustomAttributeEntityType]?.find(
          (attr) => attr.id === editingId,
        )
      : undefined;

    if (customAttribute) {
      setFormData({
        name: customAttribute.name,
        dataType: customAttribute.dataType,
        selectOptions: customAttribute.constraints?.selectOptions ?? undefined,
        objectType: customAttribute.constraints?.objectType ?? undefined,
        multipleValues:
          customAttribute.constraints?.multipleValues ?? undefined,
        required: customAttribute.constraints?.required ?? undefined,
        unique: customAttribute.constraints?.unique ?? undefined,
        minLength: customAttribute.constraints?.minLength ?? undefined,
        maxLength: customAttribute.constraints?.maxLength ?? undefined,
        minValue: customAttribute.constraints?.minValue ?? undefined,
        maxValue: customAttribute.constraints?.maxValue ?? undefined,
      });
    }
  }, [customAttributes, editingId, entityType, setFormData]);

  // 🔵🔵🔵 Focus on name input when opening the dialog
  React.useLayoutEffect(() => {
    if (showCreationDialog || !!editingId) {
      nameInputRef.current?.querySelector('input')?.focus();
    }
  }, [showCreationDialog, editingId]);

  // 🔵🔵🔵 Focus on new input when adding a new option
  React.useLayoutEffect(() => {
    if (!formData.selectOptions?.length) return;

    const newInput =
      selectOptionsRef.current?.querySelectorAll('input')[
        formData.selectOptions?.length - 1
      ];

    if (newInput) {
      (newInput as HTMLInputElement).focus();
    }
  }, [formData.selectOptions]);

  return (
    <ConsoleDialog
      className="@CustomAttributeDialog"
      title={t.customAttributesView.creationDialog.title}
      isOpen={shouldShowDialog}
      onOpenChange={handleDialogOpenChange}
      content={
        <form autoFocus className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4">
            <TextField
              ref={nameInputRef}
              label={t.customAttributesView.creationDialog.fields.name}
              value={formData.name}
              onChange={handleFieldChange('name')}
            />
            <SelectEnum<FieldDataType>
              enumValue={enumFieldDataType}
              label={t.customAttributesView.creationDialog.fields.dataType}
              value={formData.dataType}
              onChange={handleFieldChange('dataType')}
              isDisabled={!!editingId}
            />
          </div>
          {canHaveObjectType && (
            <div className="grid grid-cols-2 gap-4">
              <SelectEnum<FieldConstraintObjectType>
                enumValue={enumFieldConstraintObjectType}
                label={t.customAttributesView.creationDialog.fields.objectType}
                value={formData.objectType}
                onChange={handleFieldChange('objectType')}
              />
            </div>
          )}
          {canHaveSelectOptions && (
            <div className="flex flex-col gap-2">
              <Label>
                {t.customAttributesView.creationDialog.fields.options}
              </Label>
              <div className="grid grid-cols-2">
                <div className="flex flex-col gap-2" ref={selectOptionsRef}>
                  {formData.selectOptions?.map((it, i) => (
                    <div className="flex w-full gap-2" key={i}>
                      <TextField
                        className="w-full"
                        value={it}
                        onChange={(v) =>
                          setFormData((form) => ({
                            ...form,
                            selectOptions: form.selectOptions?.map(
                              (it, index) => (index === i ? v : it),
                            ),
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown' || e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            handleAddOption();
                          }
                        }}
                      />
                      <Button
                        variant="destructive"
                        onClick={() => {
                          setFormData((form) => ({
                            ...form,
                            selectOptions: form.selectOptions?.filter(
                              (_, index) => index !== i,
                            ),
                          }));
                        }}
                      >
                        <LucideTrash2 size={16} />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" onClick={handleAddOption}>
                    {t.customAttributesView.creationDialog.fields.addOption}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {canHaveLengthConstraint && (
            <div className="grid grid-cols-2 gap-4">
              <NumberField
                label={t.customAttributesView.creationDialog.fields.minLength}
                value={formData.minLength}
                onChange={handleFieldChange('minLength')}
              />
              <NumberField
                label={t.customAttributesView.creationDialog.fields.maxLength}
                value={formData.maxLength}
                onChange={handleFieldChange('maxLength')}
              />
            </div>
          )}
          {canHaveMinMaxValueConstraint && (
            <div className="grid grid-cols-2 gap-4">
              <NumberField
                label={t.customAttributesView.creationDialog.fields.minValue}
                value={formData.minValue}
                onChange={handleFieldChange('minValue')}
              />
              <NumberField
                label={t.customAttributesView.creationDialog.fields.maxValue}
                value={formData.maxValue}
                onChange={handleFieldChange('maxValue')}
              />
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Label>
              {t.customAttributesView.creationDialog.fields.constraints}
            </Label>
            <div className="flex flex-col gap-1">
              {canHaveSelectOptions && (
                <Checkbox
                  isSelected={formData.multipleValues}
                  onChange={handleFieldChange('multipleValues')}
                >
                  {
                    t.customAttributesView.creationDialog.fields
                      .allowMultipleValues
                  }
                </Checkbox>
              )}
              <Checkbox
                isSelected={formData.required}
                onChange={handleFieldChange('required')}
              >
                {t.customAttributesView.creationDialog.fields.required}
              </Checkbox>
              {canHaveUniqueConstraint && (
                <Checkbox
                  isSelected={formData.unique}
                  onChange={handleFieldChange('unique')}
                >
                  {t.customAttributesView.creationDialog.fields.unique}
                </Checkbox>
              )}
            </div>
          </div>
        </form>
      }
      footer={
        <Button
          isDisabled={!canSubmitForm}
          onClick={editingId ? handleUpdate : handleCreate}
          isLoading={isCreatingCustomAttribute || isUpdatingCustomAttribute}
        >
          {t.customAttributesView.creationDialog.save}
        </Button>
      }
    />
  );
};
